import React from 'react';
import {
  Button,
  Dropdown,
  Option,
  Text,
  Input,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ChevronLeft24Regular,
  ChevronRight24Regular,
  Previous24Regular,
  Next24Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  centerSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pageInput: {
    width: '60px',
  },
  infoText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
});

interface AdvancedPaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export const AdvancedPagination: React.FC<AdvancedPaginationProps> = ({
  currentPage,
  pageSize,
  totalItems,
  pageSizeOptions = [25, 50, 100, 200],
  onPageChange,
  onPageSizeChange,
}) => {
  const styles = useStyles();

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const [pageInputValue, setPageInputValue] = React.useState(currentPage.toString());

  React.useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  const handleFirstPage = () => {
    onPageChange(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    onPageChange(totalPages);
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value);
  };

  const handlePageInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const pageNumber = parseInt(pageInputValue, 10);
      if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
        onPageChange(pageNumber);
      } else {
        setPageInputValue(currentPage.toString());
      }
    }
  };

  const handlePageInputBlur = () => {
    const pageNumber = parseInt(pageInputValue, 10);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
    } else {
      setPageInputValue(currentPage.toString());
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.leftSection}>
        <Text className={styles.infoText}>Itens por página:</Text>
        <Dropdown
          value={pageSize.toString()}
          selectedOptions={[pageSize.toString()]}
          onOptionSelect={(_, data) => {
            const newSize = parseInt(data.optionValue || '50', 10);
            onPageSizeChange(newSize);
          }}
        >
          {pageSizeOptions.map((size) => (
            <Option key={size} value={size.toString()} text={size.toString()}>
              {size}
            </Option>
          ))}
        </Dropdown>
      </div>

      <div className={styles.centerSection}>
        <Text className={styles.infoText}>
          Mostrando {startItem}-{endItem} de {totalItems} {totalItems === 1 ? 'item' : 'itens'}
        </Text>
      </div>

      <div className={styles.rightSection}>
        <Button
          icon={<Previous24Regular />}
          appearance="subtle"
          disabled={currentPage === 1}
          onClick={handleFirstPage}
          title="Primeira página"
        />
        <Button
          icon={<ChevronLeft24Regular />}
          appearance="subtle"
          disabled={currentPage === 1}
          onClick={handlePreviousPage}
          title="Página anterior"
        />

        <Text className={styles.infoText}>Página</Text>
        <Input
          className={styles.pageInput}
          value={pageInputValue}
          onChange={handlePageInputChange}
          onKeyPress={handlePageInputKeyPress}
          onBlur={handlePageInputBlur}
          type="number"
          min={1}
          max={totalPages}
        />
        <Text className={styles.infoText}>de {totalPages}</Text>

        <Button
          icon={<ChevronRight24Regular />}
          appearance="subtle"
          disabled={currentPage === totalPages}
          onClick={handleNextPage}
          title="Próxima página"
        />
        <Button
          icon={<Next24Regular />}
          appearance="subtle"
          disabled={currentPage === totalPages}
          onClick={handleLastPage}
          title="Última página"
        />
      </div>
    </div>
  );
};

export default AdvancedPagination;
